import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Save } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "./StatusBadge";

export function DocumentDialog({ docId, statuses, onClose, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [body, setBody] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [legal, setLegal] = useState(false);

  const apply = (d) => {
    setDoc(d);
    setBody(d.versions.find((v) => v.version === d.current_version)?.body || "");
    setStatus(d.status);
  };
  useEffect(() => { if (docId) api.get(`/governance/documents/${docId}`).then((r) => apply(r.data)); }, [docId]);

  const run = async (fn, msg) => {
    try { apply((await fn()).data); toast.success(msg); onChanged(); setNote(""); setLegal(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveVersion = () => run(() => api.post(`/governance/documents/${docId}/versions`, { body, change_note: note }), "New draft version saved");
  const saveStatus = () => run(() => api.patch(`/governance/documents/${docId}/status`, { status, note, legal_review_confirmed: legal }), "Status updated");
  const download = async (version) => {
    try {
      const r = await api.get(`/governance/documents/${docId}/pdf`, { params: { version }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `${doc.key}-v${version}.pdf`;
      a.click();
    } catch {
      toast.error("PDF export failed. Please try again.");
    }
  };

  return (
    <Dialog open={!!docId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92dvh] max-w-3xl overflow-y-auto" data-testid="document-dialog">
        {!doc ? (
          <DialogHeader><DialogTitle>Loading document…</DialogTitle></DialogHeader>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2">
                {doc.title} <span className="text-sm font-normal text-slate-500">v{doc.current_version}</span>
                <StatusBadge status={doc.status} testId="document-status" />
              </DialogTitle>
            </DialogHeader>
            {doc.status !== "Approved" && (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold uppercase tracking-wide text-red-700" data-testid="draft-banner">
                Draft — pending owner approval and qualified UK legal review. Not issued to customers.
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Content (# heading, - bullet)</Label>
              <Textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)} className="font-mono text-xs" data-testid="document-body" />
            </div>
            <div className="space-y-1.5">
              <Label>Change note / status note</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Describe the change" data-testid="document-note" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={saveVersion} variant="outline" data-testid="document-save-version-btn"><Save className="mr-1.5 h-4 w-4" />Save as new version</Button>
              <Button onClick={() => download(doc.current_version)} variant="outline" data-testid="document-pdf-btn"><Download className="mr-1.5 h-4 w-4" />PDF</Button>
            </div>
            <div className="space-y-3 rounded-md border border-slate-200 p-3">
              <Label>Lifecycle status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="document-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              {status === "Approved" && (
                <label className="flex items-start gap-2 text-sm text-slate-700">
                  <Checkbox checked={legal} onCheckedChange={(v) => setLegal(!!v)} data-testid="document-legal-confirm" />
                  I confirm qualified UK legal review has been completed and I am authorised to approve this document.
                </label>
              )}
              <Button onClick={saveStatus} className="bg-accent font-bold hover:bg-accent/90" data-testid="document-status-save-btn">Update status</Button>
            </div>
            <div>
              <Label>Version history</Label>
              <div className="mt-2 space-y-1" data-testid="document-versions">
                {[...doc.versions].reverse().map((v) => (
                  <div key={v.version} className="flex items-center justify-between gap-2 text-xs text-slate-600">
                    <span><b>v{v.version}</b> · {v.change_note} · {v.created_by} · {new Date(v.created_at).toLocaleString()}</span>
                    <button className="text-accent underline" onClick={() => download(v.version)} data-testid={`document-version-pdf-${v.version}`}>PDF</button>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
